<template>
    <section class="leafly-section bg-gray-100 packaging-category">
        <div class="container mx-auto px-4">
            <div class="flex flex-col md:flex-row justify-between items-start">
                <div class="block">
                    <h2 class="leafly-section-title">One for all solution for custom printed packaging</h2>
                    <p class="leafly-section-second-title">Get everything custom packaging your business needs all in one place.</p>
                </div>
                <div class="hidden md:block mt-[-12px] md:mt-0">
                    <div class="flex items-center">
                        <nuxt-link :to="'/custom-packaging'" class="flex items-center text-green-600 font-weight-600 hover:text-green-700 mt-4">
                            CUSTOM PACKAGING
                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 200 200" style="enable-background: new 0 0 200 200; width: 24px; height: 24px" xml:space="preserve">
                                <path fill="currentColor" d="M151.6,51.7l0,98.1c0,0.9-0.7,1.6-1.6,1.6c-0.4,0-0.8-0.2-1.1-0.5L147,149c-0.6-0.6-0.9-1.4-0.9-2.2l0-86.8l-90.5,90.6c-0.6,0.6-1.6,0.6-2.2,0l-2.1-2.1c-0.6-0.6-0.6-1.6,0-2.2l90.6-90.6l-86.8,0c-0.8,0-1.6-0.3-2.2-0.9l-1.9-1.9c-0.6-0.6-0.6-1.6,0-2.2c0.3-0.3,0.7-0.5,1.1-0.5l98.1,0C150.9,50.2,151.6,50.9,151.6,51.7L151.6,51.7z" />
                            </svg>
                        </nuxt-link>
                    </div>
                </div>
            </div>

            <!-- Swiper 滚动区域 -->
            <div class="swiper-container-wrapper">
                <!-- Swiper 容器 -->
                <Swiper
                    :modules="swiperModules"
                    :slides-per-view="'auto'"
                    :free-mode="true"
                    :grab-cursor="true"
                    @swiper="onSwiperInit"
                    @slideChange="onSlideChange"
                    @progress="onProgress"
                    class="swiper"
                >
                    <SwiperSlide 
                        v-for="(navItem, index) in navItemVoList" 
                        :key="index" 
                        class="swiper-slide"
                    >
                        <div class="category-card mt-4 img-flip" :style="{ background: navItem.background }">
                            <div class="p-4 flex-1 relative z-10">
                                <nuxt-link :to="navItem.linkUrl" class="text-base text-gray-800 hover:text-green-700 block pb-4 text-[22px] font-weight-600 py-2" style="min-height: 48px; display: flex; align-items: center;">
                                    {{ navItem.name }}
                                </nuxt-link>

                                <div class="grid grid-cols-3 gap-x-2 gap-y-2">
                                    <nuxt-link v-for="(subCategory, subIndex) in navItem.children" :key="subIndex" :to="subCategory.linkUrl" class="text-[1rem] text-gray-600 hover:text-green-700 whitespace-nowrap hover:font-semibold py-2 px-1 block" style="line-height: 1.25rem; min-height: 44px; display: flex; align-items: center;">
                                        {{ subCategory.name }}
                                    </nuxt-link>
                                </div>
                            </div>

                            <div class="px-3 pb-4 pt-0 flex-shrink-0 mt-[-3rem] relative z-0">
                                <img :src="navItem.imageUrl" :alt="navItem.name" class="w-full object-contain" loading="lazy" />
                            </div>
                        </div>
                    </SwiperSlide>
                </Swiper>
            </div>

            <!-- 底部游标和导航 -->
            <div v-if="showScrollbar" class="scrollbar-container">
                <div class="scrollbar-track" ref="scrollbarTrackRef">
                    <div 
                        class="scrollbar-thumb" 
                        :style="{ 
                            width: scrollbarThumbWidth + '%',
                            left: scrollbarThumbLeft + '%'
                        }"
                    ></div>
                </div>

                <button 
                    class="scrollbar-nav-btn scrollbar-nav-btn-prev"
                    @click="slidePrev"
                    :disabled="!canSlidePrev"
                    aria-label="Previous"
                >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <button 
                    class="scrollbar-nav-btn scrollbar-nav-btn-next"
                    @click="slideNext"
                    :disabled="!canSlideNext"
                    aria-label="Next"
                >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import type { NavItem, NavItemVo } from '~/types'
    import cmsUtils from '~/utils/cmsUtils'
    import { ref, computed, onMounted, nextTick } from 'vue'
    import { Swiper, SwiperSlide } from 'swiper/vue'
    import { FreeMode } from 'swiper/modules'
    import type { Swiper as SwiperType } from 'swiper'

    const cmsInfo = await cmsUtils.getCmsInfo()
    const navItemVoList = ref<NavItemVo[]>(cmsUtils.getNavItemTreeByNavIndex('index_category', cmsInfo))
    for (let i = 0; i < navItemVoList.value.length; i++) {
        if (i == 0) navItemVoList.value[i]!.background = 'rgba(187, 226, 217, 1)'
        if (i == 1) navItemVoList.value[i]!.background = 'rgba(236, 201, 163, 1)'
        if (i == 2) navItemVoList.value[i]!.background = 'rgba(168, 193, 234, 1)'
        if (i == 3) navItemVoList.value[i]!.background = 'rgba(232, 177, 184, 1)'
        if (i == 4) navItemVoList.value[i]!.background = 'rgba(196, 224, 169, 1)'
        if (i == 5) navItemVoList.value[i]!.background = 'rgba(239, 188, 230, 1)'
    }

    // Swiper 模块配置
    const swiperModules = [FreeMode]

    // Swiper 实例
    const swiperInstance = ref<SwiperType | null>(null)
    const scrollbarTrackRef = ref<HTMLElement | null>(null)

    // 游标相关状态
    const showScrollbar = ref(true)
    const scrollbarThumbWidth = ref(0)
    const scrollbarThumbLeft = ref(0)
    const canSlidePrev = ref(false)
    const canSlideNext = ref(false)

    // Swiper 初始化
    function onSwiperInit(swiper: SwiperType) {
        swiperInstance.value = swiper
        updateScrollbar()
        updateNavigationButtons()
    }

    // 滑动变化时更新游标
    function onSlideChange() {
        updateScrollbar()
        updateNavigationButtons()
    }

    // 进度变化时更新游标（实时更新）
    function onProgress() {
        updateScrollbar()
    }

    // 更新游标显示
    function updateScrollbar() {
        if (!swiperInstance.value) return

        const swiper = swiperInstance.value
        const wrapper = swiper.wrapperEl
        const container = swiper.el

        if (!wrapper || !container) return

        // 计算是否可以滚动
        const wrapperWidth = wrapper.scrollWidth
        const containerWidth = container.clientWidth
        const canScroll = wrapperWidth > containerWidth
        showScrollbar.value = canScroll

        if (canScroll) {
            // 计算游标宽度（可见区域占总内容的比例，减半）
            const visibleRatio = (containerWidth / wrapperWidth) * 100
            scrollbarThumbWidth.value = Math.max(visibleRatio / 2, 5) // 最小宽度 5%
            
            // 计算游标位置（progress=0时游标在左侧，progress=1时游标在右侧）
            const progress = swiper.progress
            scrollbarThumbLeft.value = progress * (100 - scrollbarThumbWidth.value)
        } else {
            scrollbarThumbWidth.value = 100
            scrollbarThumbLeft.value = 0
        }
    }

    // 更新导航按钮状态
    function updateNavigationButtons() {
        if (!swiperInstance.value) return
        // 左箭头：可以向左滑动（回到开始） = 不在开始位置
        canSlidePrev.value = !swiperInstance.value.isBeginning
        // 右箭头：可以向右滑动（到��束） = 不在结束位置
        canSlideNext.value = !swiperInstance.value.isEnd
    }

    // 向左滑动（产品向右移动，回到开始）
    function slidePrev() {
        if (swiperInstance.value) {
            swiperInstance.value.slidePrev()
        }
    }

    // 向右滑动（产品向左移动，到结束）
    function slideNext() {
        if (swiperInstance.value) {
            swiperInstance.value.slideNext()
        }
    }

    // 组件挂载后初始化
    onMounted(() => {
        nextTick(() => {
            // 监听窗口大小变化
            window.addEventListener('resize', () => {
                updateScrollbar()
                updateNavigationButtons()
            })
        })
    })
</script>

<style scoped lang="scss">
    .leafly-section {
        padding-bottom: 0px !important;
        overflow-x: hidden;
    }

    /* Swiper 容器包装器 */
    .swiper-container-wrapper {
        position: relative;
        width: 100%;
    }

    /* Swiper 样式 */
    .swiper {
        overflow: visible;
        :deep(.swiper-wrapper) {
            display: flex;
        }
        
        .swiper-slide {
            width:340px;
            flex-shrink: 0;
            padding-right:8px;
        }
    }


    /* 分类卡片 */
    .category-card {
        background-color: white;
        border-radius: 10px;
        overflow: hidden;
        transition: box-shadow 0.3s ease;
        height:440px;
    }

    .img-flip {
        transition: transform 0.3s ease, box-shadow 0.3s ease;

        &:hover {
            transform: translateY(-12px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
        }
    }


    /* 底部游标容器 */
    .scrollbar-container {
        display: flex;
        align-items: center;
        gap: 36px;
        margin-top: 20px;
    }

    /* 游标轨道 */
    .scrollbar-track {
        flex: 1;
        height: 4px;
        background: #e5e7eb;
        border-radius: 2px;
        position: relative;
        overflow: hidden;
    }

    /* 游标滑块 */
    .scrollbar-thumb {
        position: absolute;
        top: 0;
        height: 100%;
        background: #059669;
        border-radius: 2px;
        transition: left 0.3s ease, width 0.3s ease;
    }

    /* 游标导航按钮 */
    .scrollbar-nav-btn {
        width: 40px;
        height: 40px;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        color: #059669;
        flex-shrink: 0;

        &:hover:not(:disabled) {
            background: #059669;
            color: white;
            border-color: #059669;
        }

        &:disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }
    }

</style>
